import { Injectable } from '@angular/core';
import { KalDate, KalSnackbarService } from '@kalidea/kaligraphi';
import gql from 'graphql-tag';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { ScheduledDueDate } from 'src/app/board/activity/activity-shared/activity-beneficiaries-orders/dialogs/activity-schedule-due-dates-viewer-dialog/activity-schedule-due-dates-viewer-dialog.component';
import { RouteActivityType, TypeActivite } from 'src/app/board/activity/activity.constants';
import { getKalDateFromObject } from 'src/app/utils/date.utils';
import { toDoubleDigit } from 'src/app/utils/number.utils';
import { environment } from 'src/environments/environment';
import {
    CommandeListeModaliteReglementModel,
    CommandeModaliteReglementAffichageModel,
    CommandeModaliteReglementBaseModel,
    CommandeModaliteReglementSaveInput,
    CommandeReglementInfosStateModel, EcheancierModelDtoInput,
    MutationCheckIfCommandeHasProduitWithModalitesReglementArgs,
    MutationDeleteCommandeModalitesReglementArgs,
    MutationGetCommandeInfoReglementArgs,
    MutationGetCommandeModaliteReglementAffichageArgs,
    MutationGetCommandeModalitesReglementArgs,
    MutationGetListeModalitesReglementCommandeAvailableArgs,
    MutationGetListeRibNonClosBenefArgs,
    MutationGetMiniDateDebutLocOrGroupeByIdCdeArgs,
    MutationGetModeleEcheancierInfosArgs,
    MutationSaveCommandeModalitesReglementArgs, MutationUpdateEcheancierArgs,
    RibModelDto,
    SelectionModeleEchancierModel
} from 'src/graphql/generated-types';

@Injectable({
    providedIn: 'root'
})
export class ActivitySettlementPreferencesService {

    constructor(private readonly graphqlService: GraphqlService,
                private readonly snackBarService: KalSnackbarService) {
    }

    hasProductSettlementPreferences$(activityType: RouteActivityType, orderId: number): Observable<boolean> {
        if (![RouteActivityType.Rentals, RouteActivityType.Groups].includes(activityType as RouteActivityType)) {
            return of(false);
        }

        return this.graphqlService.mutateOne<'CheckIfCommandeHasProduitWithModalitesReglement', MutationCheckIfCommandeHasProduitWithModalitesReglementArgs>({
            mutation: gql`
                mutation CheckIfCommandeHasProduitWithModalitesReglement($contexte: ContextInfosInput $idCde: Float) {
                    CheckIfCommandeHasProduitWithModalitesReglement(contexte: $contexte idCde: $idCde)
                }
            `,
            variables: {
                idCde: orderId
            }
        });
    }

    getSettlementInformationsAndPreferences$(
        orderId: number
    ): Observable<{
        orderPreferences: CommandeModaliteReglementBaseModel,
        informations: CommandeListeModaliteReglementModel
    }> {
        return forkJoin({
            orderPreferences: this.getSettlementPreference$(orderId),
            informations: this.getSettlementPreferencesInformations$(orderId)
        });
    }

    getSettlementPreference$(orderId: number): Observable<CommandeModaliteReglementBaseModel> {
        return this.graphqlService.mutateOne<'GetCommandeModalitesReglement', MutationGetCommandeModalitesReglementArgs>({
            mutation: gql`
                mutation GetCommandeModalitesReglement($contexte: ContextInfosInput $idCommande: Float) {
                    GetCommandeModalitesReglement(
                        contexte: $contexte
                        idCommande: $idCommande
                    ) {
                        IdModeleEcheancier
                        IdMoyenPaiement
                        IdRib
                    }
                }
            `,
            variables: {
                idCommande: orderId
            }
        });
    }

    getSettlementPreferenceForDisplay$(orderId: number): Observable<CommandeModaliteReglementAffichageModel> {
        return this.graphqlService.mutateOne<'GetCommandeModaliteReglementAffichage', MutationGetCommandeModaliteReglementAffichageArgs>({
            mutation: gql`
                mutation GetCommandeModaliteReglementAffichage($contexte: ContextInfosInput $idCommande: Float) {
                    GetCommandeModaliteReglementAffichage(
                        contexte: $contexte
                        idCommande: $idCommande
                    ) {
                        IdCommande
                        EcheancierLibelle
                        MoyenPaiementLibelle
                    }
                }
            `,
            variables: {
                idCommande: orderId
            }
        });
    }

    getSettlementPreferenceInformations$(orderId: number): Observable<CommandeReglementInfosStateModel> {
        return this.graphqlService.mutateOne<'GetCommandeInfoReglement', MutationGetCommandeInfoReglementArgs>({
            mutation: gql`
                mutation GetCommandeInfoReglement($contexte: ContextInfosInput $idCommande: Float) {
                    GetCommandeInfoReglement(
                        contexte: $contexte
                        idCommande: $idCommande
                    ) {
                        AreEcheanceGenerated
                        IsFactureCommandeSoldee
                    }
                }
            `,
            variables: {
                idCommande: orderId
            }
        });
    }

    deleteSettlementPreference$(orderId: number): Observable<string> {
        return this.graphqlService.mutateOne<'DeleteCommandeModalitesReglement', MutationDeleteCommandeModalitesReglementArgs>({
            mutation: gql`
                mutation DeleteCommandeModalitesReglement($contexte: ContextInfosInput $idCommande: Float) {
                    DeleteCommandeModalitesReglement(contexte: $contexte idCommande: $idCommande)
                }
            `,
            variables: {
                idCommande: orderId
            }
        });
    }

    getSettlementPreferencesInformations$(orderId: number): Observable<CommandeListeModaliteReglementModel> {
        return this.graphqlService.mutateOne<'GetListeModalitesReglementCommandeAvailable', MutationGetListeModalitesReglementCommandeAvailableArgs>({
            mutation: gql`
                mutation GetListeModalitesReglementCommandeAvailable($contexte: ContextInfosInput $idCommande: Float) {
                    GetListeModalitesReglementCommandeAvailable(contexte: $contexte idCommande: $idCommande) {
                        ListeModeleEcheancier {
                            ModeleEcheancierDefautModel {
                                IdModeleEcheancier
                                Libelle
                                IsParDefaut
                                IsClos
                            }
                        }
                        ListeMoyenPaiement {
                            MoyenPaiementDefautModel {
                                Id
                                Code
                                Libelle
                                IsParDefaut
                            }
                        }
                    }
                }
            `,
            variables: {
                idCommande: orderId
            }
        });
    }

    getIbansList$(orderId: number): Observable<RibModelDto[]> {
        return this.graphqlService.mutateOne<'GetListeRibNonClosBenef', MutationGetListeRibNonClosBenefArgs>({
            mutation: gql`
                mutation GetListeRibNonClosBenef($contexte: ContextInfosInput $idCommande: Float) {
                    GetListeRibNonClosBenef(contexte: $contexte idCommande: $idCommande) {
                        RibModelDTO {
                            IdRib
                            Codpay
                            BBAN
                            Cle
                        }
                    }
                }
            `,
            variables: {
                idCommande: orderId
            }
        }).pipe(
            map((GetListeRibNonClosBenef) => GetListeRibNonClosBenef?.RibModelDTO || [])
        );
    }

    getScheduleDueDates({
                            scheduleId,
                            orderId,
                            amount
                        }: { scheduleId: number, orderId: number, amount: number }): Observable<ScheduledDueDate[]> {
        return this.getScheduleModelInfos$(scheduleId).pipe(
            // récupération de la date de 1er échéance.
            switchMap(scheduleModel => {
                // Cas où le début des échéances est déterminé en fonction de la commande.
                if (scheduleModel.NombreJoursDeclenchement) {
                    const operation = scheduleModel.IsDeclenchementAvant ? 'substract' : 'add';
                    return this.getOrderStartDate$(orderId).pipe(
                        map(date => ({
                            firstDueDate: date[operation](scheduleModel.NombreJoursDeclenchement, 'days'),
                            scheduleModel
                        }))
                    );
                }

                return of({firstDueDate: this.computeFirstDateFromToday(scheduleModel), scheduleModel});
            }),
            // On met à jour le dataSource en fonction de la date.
            map(({firstDueDate, scheduleModel}) => this.generateDueDatesDataSource(scheduleModel, firstDueDate, amount))
        );
    }

    /**
     * @todo peut être regarder pour factoriser avec l'appel effectué dans sharde-edit-repayment-schedule-dialog
     * dans un autre service
     */
    getScheduleModelInfos$(scheduleModelId: number): Observable<SelectionModeleEchancierModel> {
        return this.graphqlService.mutateOne<'GetModeleEcheancierInfos', MutationGetModeleEcheancierInfosArgs>({
            mutation: gql`
                mutation GetModeleEcheancierInfos($contexte: ContextInfosInput $idModeleEcheancier: Int) {
                    GetModeleEcheancierInfos(contexte: $contexte idModeleEcheancier: $idModeleEcheancier) {
                        ...on SelectionModeleEchancierModel {
                            IsDeclenchementAvant
                            NombreJoursDeclenchement
                            MoisPremiereEcheance
                            JourDansMois
                            DelaiPremiereEcheance
                            Periodicite
                            PourcentagePremiereEcheance
                            MontantPremiereEcheance
                            NombreEcheance
                        }
                    }
                }
            `,
            variables: {
                idModeleEcheancier: scheduleModelId
            }
        });
    }

    getOrderStartDate$(orderId: number): Observable<KalDate> {
        return this.graphqlService.mutateOne<'GetMiniDateDebutLocOrGroupeByIdCde', MutationGetMiniDateDebutLocOrGroupeByIdCdeArgs>({
            mutation: gql`
                mutation GetMiniDateDebutLocOrGroupeByIdCde($contexte: ContextInfosInput $IdCde: Float) {
                    GetMiniDateDebutLocOrGroupeByIdCde(contexte: $contexte IdCde: $IdCde)
                }
            `,
            variables: {
                IdCde: orderId
            }
        }).pipe(
            map(GetMiniDateDebutLocOrGroupeByIdCde => getKalDateFromObject({date: GetMiniDateDebutLocOrGroupeByIdCde}, 'date'))
        );
    }

    saveSettlementPreferenceAndGetDisplayData(settlementPreference: CommandeModaliteReglementSaveInput): Observable<CommandeModaliteReglementAffichageModel> {
        return this.saveOrderSettlementPreference$(settlementPreference).pipe(
            tap(() => this.snackBarService.open({title: 'La préférence de règlement a bien été enregistrée.'})),
            switchMap(e => this.getSettlementPreferenceForDisplay$(settlementPreference.IdCommande))
        );
    }

    private saveOrderSettlementPreference$(settlementPreference: CommandeModaliteReglementSaveInput): Observable<number> {
        return this.graphqlService.mutateOne<'SaveCommandeModalitesReglement', MutationSaveCommandeModalitesReglementArgs>({
            mutation: gql`
                mutation SaveCommandeModalitesReglement($contexte: ContextInfosInput $modeleCommandeModalite: CommandeModaliteReglementSaveInput) {
                    SaveCommandeModalitesReglement(contexte: $contexte modeleCommandeModalite: $modeleCommandeModalite)
                }
            `,
            variables: {
                modeleCommandeModalite: settlementPreference
            }
        });
    }

    /**
     * calcul de la date de 1ère échéance en fonction de la date du jour.
     */
    private computeFirstDateFromToday(scheduleModel: SelectionModeleEchancierModel): KalDate {
        const today = new KalDate();
        const {
            MoisPremiereEcheance,
            JourDansMois,
            DelaiPremiereEcheance
        } = scheduleModel || {};

        const year = today.getYear();
        const month = MoisPremiereEcheance ?? today.getMonth();
        const day = JourDansMois ?? today.getDay();

        let dueDate = new KalDate(
            `${day > 10 ? day : '0' + day}/${month > 10 ? month : '0' + month}/${year}`,
            environment.date.format.display
        );

        if (!!DelaiPremiereEcheance) {
            dueDate = dueDate.add(DelaiPremiereEcheance, 'months');
        }

        if (dueDate.isBefore(today)) {
            dueDate = dueDate.add(1, 'years');
        }

        return dueDate;
    }

    private generateDueDatesDataSource(scheduleModel: SelectionModeleEchancierModel, startDate: KalDate, amount: number): ScheduledDueDate[] {
        const {
            NombreEcheance = 1,
            Periodicite = 1,
        } = scheduleModel || {};
        const dueDates = [];

        const getAmount = this.getAmountGenerator(scheduleModel, amount);

        for (let i = 1; i <= NombreEcheance; i++) {
            dueDates.push({
                number: i,
                date: startDate.add((i - 1) * Periodicite, 'months'),
                amount: getAmount.next().value
            });
        }

        return dueDates;
    }

    /**
     * Générateur pour les montant des échéances.
     * permet d'éviter de recalculer firstDueAmount, totalMinusFirstAmount, dueAmount à chaque appel.
     * @link https://developer.mozilla.org/fr/docs/Web/JavaScript/Reference/Global_Objects/Generator
     */
    private* getAmountGenerator(scheduleModel: SelectionModeleEchancierModel, amount: number): IterableIterator<number> {
        const {
            NombreEcheance = 1,
            PourcentagePremiereEcheance,
            MontantPremiereEcheance,
        } = scheduleModel || {};
        const totalAmount = amount;

        // calcul de la 1ère échéance
        const firstDueAmount = !!PourcentagePremiereEcheance
            ? toDoubleDigit(totalAmount * PourcentagePremiereEcheance) / 100
            : !!MontantPremiereEcheance
                ? toDoubleDigit(MontantPremiereEcheance)
                : toDoubleDigit(totalAmount / NombreEcheance);

        // le premier appel à next() retourne le 1er montant.
        yield firstDueAmount;

        // calcul du montant des échéances entre la 1ère et dernière.
        const totalMinusFirstAmount = totalAmount - firstDueAmount;
        const dueAmount = toDoubleDigit(totalMinusFirstAmount / (NombreEcheance - 1));

        // Pour toutes les échéances entre la première et la dernière (exclues), on retourne le même montant.
        for (let i = 1; i < NombreEcheance - 1; i++) {
            yield dueAmount;
        }

        // pour la dernière échéance on calcule le montant restant.
        yield toDoubleDigit(totalMinusFirstAmount - dueAmount * (NombreEcheance - 2));
    }


}
